<template>
  <div class="as-employee-checkpoint-inspection-card" :class="[railClass, bodyToneClass]">
    <ChatterDrawer
      :drawer="chatterDrawerOpen"
      :avatar="chatterAvatar"
      :isEmployeeChatter="true"
      :type="chatterTypeName"
      :typeValue="chatterTypeValue"
      :listData="chatterListData"
      object-type="checkpoint_evaluation"
      :object-id="resolvedInspectionId"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalChatter"
      @closeDrawer="onChatterDrawerClose"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
    <div class="as-employee-checkpoint-inspection-card__rail" aria-hidden="true" />

    <div class="as-employee-checkpoint-inspection-card__body">
      <span class="as-employee-checkpoint-inspection-card__title">{{ title }}</span>

      <div class="as-employee-checkpoint-inspection-card__meta">
        <ul class="as-employee-checkpoint-inspection-card__badges">
          <li
            v-if="activeFacets.isInspectorOnly"
            class="as-employee-checkpoint-inspection-card__badge-item"
          >
            <InspectorOnlyBadge />
          </li>
          <li
            v-if="showStatusIndicator && checkpointStatus"
            class="as-employee-checkpoint-inspection-card__badge-item"
          >
            <InspectionStatusIndicatorBadge :checkpoint-status="checkpointStatus" />
          </li>
          <li
            v-if="activeFacets.media === 'video'"
            class="as-employee-checkpoint-inspection-card__badge-item"
          >
            <InspectionVideoWatchStatusBadge :learning-seen="learningSeen" />
          </li>
          <li
            v-else-if="activeFacets.media === 'article'"
            class="as-employee-checkpoint-inspection-card__badge-item"
          >
            <InspectionArticleReadStatusBadge
              :ideaMDP452="ideaMDP452"
              :learning-seen="learningSeen"
            />
          </li>
          <li v-if="showQuizBadge" class="as-employee-checkpoint-inspection-card__badge-item">
            <InspectionQuizStatusBadge :quiz-status="quizBadgeStatus" />
          </li>
          <li v-if="showInspectedBadge" class="as-employee-checkpoint-inspection-card__badge-item">
            <InspectionInspectedStatusBadge
              :ideaMDP452="ideaMDP452"
              :inspection-status="inspectedBadgeStatus"
            />
          </li>
        </ul>
      </div>

      <p v-if="helperCopy" class="as-employee-checkpoint-inspection-card__helper">
        {{ helperCopy }}
      </p>
    </div>

    <div class="as-employee-checkpoint-inspection-card__actions">
      <div
        v-if="resolvedActions.length"
        class="as-employee-checkpoint-inspection-card__action-stack"
      >
        <StandardButton
          v-for="action in resolvedActions"
          :key="action.id"
          :type="action.type"
          :variant="action.variant"
          :size="action.size || 'small'"
          :label="action.label"
          class="as-employee-checkpoint-inspection-card__action-btn"
          @click="onActionClick(action)"
        />
      </div>

      <button
        type="button"
        class="as-employee-checkpoint-inspection-card__chatter"
        aria-label="Open chatter"
        @click="onChatterClick"
      >
        <img :src="chatterIconSrc" alt="" width="24" height="24" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import InspectionStatusIndicatorBadge from "@components/InspectionStatusIndicatorBadge/InspectionStatusIndicatorBadge.vue";
import InspectionVideoWatchStatusBadge from "@components/InspectionVideoWatchStatusBadge/InspectionVideoWatchStatusBadge.vue";
import InspectionArticleReadStatusBadge from "@components/InspectionArticleReadStatusBadge/InspectionArticleReadStatusBadge.vue";
import InspectionQuizStatusBadge from "@components/InspectionQuizStatusBadge/InspectionQuizStatusBadge.vue";
import InspectionInspectedStatusBadge from "@components/InspectionInspectedStatusBadge/InspectionInspectedStatusBadge.vue";
import InspectorOnlyBadge from "@components/InspectorOnlyBadge/InspectorOnlyBadge.vue";
import { checkpointStatusForCardStatus } from "@components/InspectionStatusIndicatorBadge/inspectionStatusIndicatorBadgeConstants";
import {
  fetchCheckpointEvaluationChatter,
  resolveAccountIdFromStorage,
  resolveCheckpointEvaluationRecordId,
} from "@components/utils/checkpointEvaluationChatter";
import {
  CARD_STATUS_LABELS,
  CARD_STATUSES,
  CARD_TYPES,
  CHIP_VARIANTS,
  RAIL_BY_STATUS,
  normalizeCardStatus,
  resolveCardTypeFacets,
} from "./asEmployeeCheckpointInspectionCardConstants";
import { BODY_BACKGROUND_BY_TONE } from "./asEmployeeCheckpointInspectionCardRails";
import {
  INSPECTED_BADGE_STATUS_BY_CARD_STATUS,
  QUIZ_BADGE_STATUS_BY_CARD_STATUS,
  buildHelperCopy,
  invalidShapeStatusWarnings,
  isLearningSeen,
} from "./asEmployeeCheckpointInspectionCardHelpers";

const chatterIconSrc = require("@components/assets/icons/chatter.svg");

const props = defineProps({
  /**
   * Legacy flattened type string ("Video + Quiz + Inspection", "Inspector-only",
   * "Not Applicable"). ER-11336: kept for one release so AsEmployeeChecklistInspectionCard
   * and Storybook stories keep working; facets are derived from it only when the
   * facet props (`media` / `hasQuiz` / `hasInspection`) are not supplied.
   */
  type: {
    type: String,
    default: "",
    validator: (value) =>
      value === "" ||
      CARD_TYPES.includes(value) ||
      String(value).toLowerCase() === "not applicable",
  },
  /**
   * Card status in either the normalised enum (`CARD_STATUSES`) or any legacy
   * spelling (code or Figma) accepted by `CARD_STATUS_LEGACY_MAP`. Unknown
   * statuses fail the validator (Vue dev warning) instead of rendering silently.
   */
  status: {
    type: String,
    required: true,
    validator: (value) => {
      const trimmed = String(value || "").trim();
      return (
        trimmed === "" || CARD_STATUSES.includes(trimmed) || normalizeCardStatus(trimmed) != null
      );
    },
  },
  title: {
    type: String,
    required: true,
  },
  /** Medium facet. Authoritative when supplied; otherwise parsed from `type`. */
  media: {
    type: String,
    default: null,
    validator: (value) => value == null || ["video", "article", "none"].includes(value),
  },
  /** Quiz facet (nullable so absence means "derive from type"). */
  hasQuiz: {
    type: Boolean,
    default: null,
  },
  /** Inspection facet (nullable so absence means "derive from type"). */
  hasInspection: {
    type: Boolean,
    default: null,
  },
  /**
   * Not Applicable is a universal card of its own (ECL: route every not-applicable
   * checkpoint of any underlying type to the one shared card). When true it
   * overrides the facets and status.
   */
  notApplicable: {
    type: Boolean,
    default: false,
  },
  /**
   * Override for the derived helper row. Escape hatch for exceptions only —
   * the copy is normally keyed off status + media + hasQuiz so it cannot drift.
   */
  helperText: {
    type: String,
    default: "",
  },
  /**
   * Feature flag `idea-MDP-452` (New Checkpoint Edit and Inspection). The card
   * surface itself is already flag-gated by the caller; this prop forwards the
   * resolved value to the flagged badge components (kill-switch semantics — the
   * card renders badges by default, as the flag-gated caller only mounts it
   * while the flag is on).
   */
  ideaMDP452: {
    type: Boolean,
    default: true,
  },
  /**
   * @deprecated ER-11336 — the MetaRow is now derived from facets + status and
   * this prop is ignored. Kept accepted (and validated) for one release so the
   * coach card's shared constants and any Storybook history keep working.
   */
  statusChips: {
    type: Array,
    default: () => [],
    validator: (chips) =>
      chips.every(
        (chip) => chip && typeof chip.label === "string" && CHIP_VARIANTS.includes(chip.variant)
      ),
  },
  /** Overrides the primary (first) action label when set. */
  actionLabel: {
    type: String,
    default: "",
  },
  /** Overrides the secondary action label when dual actions are shown. */
  secondaryActionLabel: {
    type: String,
    default: "",
  },
  /** Account id for checkpoint_evaluation chatter; falls back to `currentAccountID` in localStorage. */
  accountId: {
    type: [String, Number],
    default: null,
  },
  /** Checkpoint evaluation / inspection id for chatter `record_id`. */
  inspectionId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["action-click", "secondary-action-click", "chatter-click"]);

/** Normalised status enum; empty string when the (invalid) status was filtered by the validator. */
const normalizedStatus = computed(() => normalizeCardStatus(props.status) || "");

/** Not Applicable is its own universal card: prop, legacy status, or "Not Applicable" type. */
const parsedTypeFacets = computed(() => resolveCardTypeFacets(props.type));

const notApplicable = computed(
  () =>
    props.notApplicable === true ||
    normalizedStatus.value === "not_applicable" ||
    parsedTypeFacets.value.notApplicable
);

const baseFacets = computed(() => {
  const parsed = parsedTypeFacets.value;
  return {
    media: props.media != null ? props.media : parsed.media,
    hasQuiz: props.hasQuiz != null ? props.hasQuiz : parsed.hasQuiz,
    hasInspection: props.hasInspection != null ? props.hasInspection : parsed.hasInspection,
    isInspectorOnly:
      parsed.isInspectorOnly ||
      (props.media === "none" && props.hasQuiz === false && props.hasInspection === true),
  };
});

/** Facets that actually drive the MetaRow; notApplicable overrides everything. */
const activeFacets = computed(() => {
  if (notApplicable.value) {
    return { media: "none", hasQuiz: false, hasInspection: false, isInspectorOnly: false };
  }
  return baseFacets.value;
});

const railTone = computed(() => RAIL_BY_STATUS[normalizedStatus.value] || "grey");

const railClass = computed(() => ({
  [`as-employee-checkpoint-inspection-card--rail-${railTone.value}`]: true,
}));

/**
 * ER-11367: certain statuses tint the body and the action column (not just the
 * 4px rail). Only tones with a tint entry in BODY_BACKGROUND_BY_TONE get a
 * modifier; the remaining statuses keep the white background.
 */
const bodyToneClass = computed(() => {
  if (!BODY_BACKGROUND_BY_TONE[railTone.value]) return {};
  return {
    [`as-employee-checkpoint-inspection-card--tone-${railTone.value}`]: true,
  };
});

/** Inspector-only cards never render the coarse status indicator badge. */
const showStatusIndicator = computed(() => !activeFacets.value.isInspectorOnly);

/** Coarse "Checkpoint …" status for the status indicator (via the badge module's own map). */
const checkpointStatus = computed(() => {
  if (!showStatusIndicator.value) return "";
  const canonical = CARD_STATUS_LABELS[normalizedStatus.value];
  return checkpointStatusForCardStatus(canonical) || "";
});

const learningSeen = computed(() => isLearningSeen(normalizedStatus.value));

const quizBadgeStatus = computed(() => {
  if (!activeFacets.value.hasQuiz) return "Not Required";
  return QUIZ_BADGE_STATUS_BY_CARD_STATUS[normalizedStatus.value] || "Not Attempted";
});

const inspectedBadgeStatus = computed(() => {
  // Inspector-only checkpoints have no inspection step facet, but their card's
  // single status signal IS the inspection badge — derive it from the status.
  if (activeFacets.value.isInspectorOnly) {
    return INSPECTED_BADGE_STATUS_BY_CARD_STATUS[normalizedStatus.value] || "inspection-pending";
  }
  if (!activeFacets.value.hasInspection) return "not-required";
  return INSPECTED_BADGE_STATUS_BY_CARD_STATUS[normalizedStatus.value] || "inspection-pending";
});

/**
 * ER-11367 #5/#6 — quiz chip is suppressed on Inspector-only cards (Figma shows
 * InspectorOnlyBadge + inspection status only) and on the universal Not
 * Applicable card (Figma shows the status indicator only).
 */
const showQuizBadge = computed(() => !notApplicable.value && !activeFacets.value.isInspectorOnly);

/**
 * ER-11367 #5/#6 — the inspected chip is suppressed on the Not Applicable card
 * only. Inspector-only checkpoints keep it: their inspection badge is the card's
 * single status signal (derived from the coarse status), exactly as Figma shows.
 */
const showInspectedBadge = computed(() => !notApplicable.value);

const helperCopy = computed(() => {
  if (props.helperText) return props.helperText;
  return buildHelperCopy({
    status: normalizedStatus.value,
    media: activeFacets.value.media,
    hasQuiz: activeFacets.value.hasQuiz,
  });
});

/**
 * ER-11336 Step 2 / AC 1 — impossible shape + status combinations warn in dev
 * instead of silently degrading to "Not Required" badges. Compiled out entirely
 * in production bundles (NODE_ENV=production), so it carries zero prod cost.
 */
if (process.env.NODE_ENV !== "production") {
  watchEffect(() => {
    const warnings = invalidShapeStatusWarnings({
      status: normalizedStatus.value,
      hasQuiz: activeFacets.value.hasQuiz,
      hasInspection: activeFacets.value.hasInspection,
      isInspectorOnly: activeFacets.value.isInspectorOnly,
      notApplicable: notApplicable.value,
    });
    warnings.forEach((warning) => {
      console.warn(`[AsEmployeeCheckpointInspectionCard] ${warning}`);
    });
  });
}

/** Actions are keyed on the normalised enum so no Figma state falls through to "no buttons". */
function defaultActionsForStatus(status) {
  switch (status) {
    case "not_started":
    case "reset":
      return [
        {
          id: "start",
          label: "Start",
          type: "magenta",
          variant: "primary",
          size: "small",
        },
      ];
    case "quiz_not_attempted":
      return [
        {
          id: "take-quiz",
          label: "Take the Quiz",
          type: "magenta",
          variant: "primary",
          size: "small",
        },
      ];
    case "quiz_failed":
      return [
        {
          id: "retake-quiz",
          label: "Retake Quiz",
          type: "red",
          variant: "primary",
          size: "small",
        },
      ];
    case "inspection_ready":
      // ER-11367 #3 — Figma shows a single primary "Prepare" button (no
      // "Ask for inspection" text link, no Review). Keeps the `ask-inspection`
      // routing id so the employee drawer still opens on the inspection step.
      return [
        {
          id: "ask-inspection",
          label: "Prepare",
          type: "magenta",
          variant: "primary",
          size: "small",
        },
      ];
    case "inspection_failed":
      // ER-11367 #3 — single primary/error "Prepare again" button.
      return [
        {
          id: "ask-inspection",
          label: "Prepare again",
          type: "red",
          variant: "primary",
          size: "small",
        },
      ];
    case "passed":
      // Passed inspector-only checkpoints carry no action (chatter only),
      // matching the Not Applicable variant (Eric Kish, 2026-09-01).
      if (activeFacets.value.isInspectorOnly) return [];
      return [
        {
          id: "review",
          label: "See learning", // ER-11367 #4 — Figma label; outline styling unchanged
          type: "magenta",
          variant: "outline",
          size: "small",
        },
      ];
    case "not_applicable":
    default:
      return [];
  }
}

const resolvedActions = computed(() => {
  const actions = defaultActionsForStatus(normalizedStatus.value).map((action) => ({ ...action }));
  if (!actions.length) return actions;

  if (props.actionLabel) {
    actions[0].label = props.actionLabel;
  }
  if (actions.length > 1 && props.secondaryActionLabel) {
    actions[1].label = props.secondaryActionLabel;
  }
  return actions;
});

function onActionClick(action) {
  emit("action-click", { id: action.id, label: action.label, status: props.status });
}

const chatterDrawerOpen = ref(false);
const chatterList = ref([]);
const totalChatter = ref(0);
const chatterPage = ref(1);
const chatterLoading = ref(false);
const chatterTypeName = ref("Checkpoint");
const chatterTypeValue = ref("");
const chatterListData = ref({});
const chatterAvatar = ref({});

const resolvedInspectionId = computed(() =>
  resolveCheckpointEvaluationRecordId({ inspectionId: props.inspectionId })
);

const resolvedAccountId = computed(() => resolveAccountIdFromStorage(props.accountId));

async function fetchChatter(page = 1, append = false) {
  const accountId = resolvedAccountId.value;
  const recordId = resolvedInspectionId.value;
  if (accountId == null || recordId == null || chatterLoading.value) return;

  chatterLoading.value = true;
  try {
    const { results, count } = await fetchCheckpointEvaluationChatter(accountId, recordId, page);
    totalChatter.value = count;
    chatterList.value = append ? [...chatterList.value, ...results] : results;
    chatterPage.value = page;
  } catch (error) {
    console.error("Error loading checkpoint evaluation chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function openChatterDrawer() {
  if (chatterLoading.value) return;

  chatterTypeValue.value = props.title || "";
  chatterListData.value = {
    id: resolvedInspectionId.value,
    criteria: props.title || "",
  };
  chatterAvatar.value = {};
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  await fetchChatter(1, false);
  chatterDrawerOpen.value = true;
}

async function loadMoreChatter() {
  if (totalChatter.value <= chatterList.value.length) return;
  await fetchChatter(chatterPage.value + 1, true);
}

function onChatterDrawerClose(data) {
  if (!data) return;
  chatterDrawerOpen.value = false;
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
}

function onChatterClick() {
  if (chatterLoading.value) return;
  emit("chatter-click");
  openChatterDrawer();
}
</script>

<style scoped>
.as-employee-checkpoint-inspection-card {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}

.as-employee-checkpoint-inspection-card__rail {
  flex: 0 0 4px;
  align-self: stretch;
  width: 4px;
  background-color: #98a2b3;
}

/* Rail colors match RAIL_COLOR_BY_TONE in asEmployeeCheckpointInspectionCardRails.js */
.as-employee-checkpoint-inspection-card--rail-grey .as-employee-checkpoint-inspection-card__rail {
  background-color: #98a2b3;
}

.as-employee-checkpoint-inspection-card--rail-orange .as-employee-checkpoint-inspection-card__rail {
  background-color: #f79009;
}

.as-employee-checkpoint-inspection-card--rail-red .as-employee-checkpoint-inspection-card__rail {
  background-color: #f04438;
}

.as-employee-checkpoint-inspection-card--rail-green .as-employee-checkpoint-inspection-card__rail {
  background-color: #17b26a;
}

.as-employee-checkpoint-inspection-card--rail-purple .as-employee-checkpoint-inspection-card__rail {
  background-color: #6734d4;
}

.as-employee-checkpoint-inspection-card--rail-blue .as-employee-checkpoint-inspection-card__rail {
  background-color: #3538cd;
}

/* Body + action-column tints mirror BODY_BACKGROUND_BY_TONE in
   asEmployeeCheckpointInspectionCardRails.js (ER-11367 #2). */
.as-employee-checkpoint-inspection-card--tone-red .as-employee-checkpoint-inspection-card__body,
.as-employee-checkpoint-inspection-card--tone-red .as-employee-checkpoint-inspection-card__actions {
  background-color: #fef3f2;
}

.as-employee-checkpoint-inspection-card--tone-green .as-employee-checkpoint-inspection-card__body,
.as-employee-checkpoint-inspection-card--tone-green
  .as-employee-checkpoint-inspection-card__actions {
  background-color: #ecfdf3;
}

.as-employee-checkpoint-inspection-card--tone-blue .as-employee-checkpoint-inspection-card__body,
.as-employee-checkpoint-inspection-card--tone-blue
  .as-employee-checkpoint-inspection-card__actions {
  background-color: #eef4ff;
}

.as-employee-checkpoint-inspection-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 14px;
}

.as-employee-checkpoint-inspection-card__title {
  color: #101828;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.as-employee-checkpoint-inspection-card__meta {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.as-employee-checkpoint-inspection-card__badges {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.as-employee-checkpoint-inspection-card__badge-item {
  display: inline-flex;
  margin: 0;
}

.as-employee-checkpoint-inspection-card__helper {
  margin: 0;
  color: #667085;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.as-employee-checkpoint-inspection-card__actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  gap: 10px;
  padding: 11px 14px;
  border-left: 1px solid #f2f4f7;
  box-sizing: border-box;
}

.as-employee-checkpoint-inspection-card__action-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.as-employee-checkpoint-inspection-card__chatter {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.as-employee-checkpoint-inspection-card__chatter:hover {
  background-color: #f2edff;
}

.as-employee-checkpoint-inspection-card__chatter:focus-visible {
  outline: 2px solid #c3b5fd;
  outline-offset: 2px;
}
</style>
